import React from "react";
import { FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import {
  EXPORT_DATA_TYPES,
  EXPORT_INCLUSION_TYPES,
  ExportFieldConfiguration,
  EXPORT_FIELDS_INCLUSION_TYPES,
} from "../types";
import { getCommonExportRatioOptions } from "../utils";
import { ExportDataTypeInclusionSelector } from "../ExportDataTypeInclusionSelector";
import { FieldConfigurationSelector } from "../FieldConfigurationSelector";
import { firmographicsExportFields } from "../exportConfigurationFields";
import { renderSpendCategoryFieldSelector } from "../hooks/renderSpendCategoryFieldSelector";

interface Props {
  dataType?: EXPORT_DATA_TYPES;
  spendInclusion: EXPORT_INCLUSION_TYPES;
  setSpendInclusion: (value: EXPORT_INCLUSION_TYPES) => void;
  universeEnabled?: boolean;
  spendFieldConfiguration?: ExportFieldConfiguration;
  setSpendFieldConfiguration?: (cfg: ExportFieldConfiguration) => void;
  spendIncludeSMB?: boolean;
  setSpendIncludeSMB?: (v: boolean) => void;
  enforceMandatoryFields?: boolean;
}

export const ITSpendOptions: React.FC<Props> = ({
  dataType = EXPORT_DATA_TYPES.SPEND,
  spendInclusion,
  setSpendInclusion,
  universeEnabled,
  spendFieldConfiguration,
  setSpendFieldConfiguration,
  spendIncludeSMB,
  setSpendIncludeSMB,
  enforceMandatoryFields,
}) => {
  return (
    <Stack spacing={2} sx={{ width: "70%", paddingTop: "40px" }}>
      <Typography variant="h5" fontWeight={500} fontSize={16}>
        {dataType}
      </Typography>
      <ExportDataTypeInclusionSelector
        dataType={dataType}
        testIdPrefix={
          dataType === EXPORT_DATA_TYPES.AI_SPEND
            ? "export-ai-spend"
            : "export-it-spend"
        }
        options={getCommonExportRatioOptions(dataType)}
        inclusion={spendInclusion}
        setInclusion={setSpendInclusion}
      />
      {universeEnabled && (
        <>
          <FieldConfigurationSelector
            fields={[]}
            firmographicsFields={firmographicsExportFields}
            value={
              spendFieldConfiguration ?? {
                inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
                fields: [],
              }
            }
            onChange={setSpendFieldConfiguration ?? (() => {})}
            title="IT Spend Categories [Universe Only]"
            checkboxLabel="Export All Spend Data Categories"
            renderFieldSelector={renderSpendCategoryFieldSelector}
            requiredFirmographicsField={
              enforceMandatoryFields
                ? {
                    value: "company_id",
                    note: "Note: Company ID will always be included in your export for proper company identification.",
                  }
                : undefined
            }
            disabled={spendInclusion === EXPORT_INCLUSION_TYPES.NONE}
          />
          <Typography variant="caption" color="text.secondary">
            Note: Spend Tier will be included in your export for proper spend
            tier identification.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={spendIncludeSMB ?? false}
                onChange={(e) => setSpendIncludeSMB?.(e.target.checked)}
                disabled={spendInclusion === EXPORT_INCLUSION_TYPES.NONE}
                data-testid="it-spend-smb-toggle"
              />
            }
            label="Include SMB Spend data [Universe Only]"
          />
        </>
      )}
    </Stack>
  );
};
